import React, { useEffect, useState } from 'react';
import { getEditorColor } from '../../util/ColorUtil';
import {
  getQuoteContainerClass,
  isItUndefined,
  modifyLinks,
} from '../../util/DataUtil';
import { cleanHTML } from '../utils/htmlUtils';
import AuthorBlock, { getPublishDate } from './AuthorBlock';
import {useRouter} from "next/router";

const isItTemplate = () => {
  if (typeof document !== 'undefined') {
    return (
      document.querySelector('.gated_template') !== null ||
      document.querySelector('.non_gated_template') !== null ||
      document.querySelector('.solution_template') !== null ||
      document.querySelector('.media_template') !== null ||
      document.querySelector('.press_release') !== null ||
      document.querySelector('.case_study_dynamic') !== null ||
      document.querySelector('.freestyle_template_article') !== null);
  }
  return false;
};

// `field_content_type_templat` term id for Blog. Shared by every locale: the
// term is translated, its id is not.
const BLOG_CONTENT_TYPE_TID = '259';

const isItContentTemplateArticle = (pageType) => {
  if (typeof document !== 'undefined' && pageType) {
    return (
      pageType === 'Articles' &&
      document.querySelector('.content_template') !== null
    );
  }
  return false;
};

const TemplateText = ({
  children,
  color,
  itemCount,
  data,
  pageType,
  authorBlockData,
  blockType,
}) => {
  const emphasizedTextColor = getEditorColor(
    isItUndefined(children?.field_slug)
  )
    ? getEditorColor(isItUndefined(children?.field_slug))
    : getEditorColor(isItUndefined(children?.field_template_text));

  const [template, setTemplate] = useState(true);
  const [isContentTemplateArticle, setIsContentTemplateArticle] =
    useState(false);
  // From props rather than an effect, so the server renders the author
  // block too.
  const authorExists = authorBlockData?.length > 0;
  const { locale } = useRouter();
  // False on the server and for the first render in the browser, true once
  // the page has hydrated.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setTemplate(isItTemplate());
    setIsContentTemplateArticle(isItContentTemplateArticle(pageType));
  }, [children]);

  // Blog articles are the only pages asked to carry their copy in the HTML
  // response. Every other template keeps the behaviour it had, so this guard
  // has to tell a blog post from a case study or a white paper — and the
  // content type cannot: all three are `non_gated_template`. The CMS content
  // taxonomy can.
  //
  // Matched on the term id, not its name. The name is translated — Korean
  // sends '블로그' and Chinese '博客' — so a name check left both of those
  // locales rendering nothing on the server while English, German, French,
  // Italian and Spanish passed. The id is the same 259 in every locale and on
  // every environment (checked on this one, UAT and production). The English
  // name is kept as a fallback in case the id ever moves.
  const contentTypeTerm = blockType?.[0];
  const isBlogArticle =
    String(contentTypeTerm?.tid || '') === BLOG_CONTENT_TYPE_TID ||
    String(contentTypeTerm?.name || '').trim().toLowerCase() === 'blog';

  // On a blog article the body is rendered on the server too. It used to
  // return nothing there, so the copy was missing from the HTML response and
  // only appeared once JavaScript ran — invisible to crawlers that do not run
  // it. Nothing here needs the DOM: cleanHTML and modifyLinks are plain
  // string replacements.
  //
  // Until hydration the copy sits in a <div>. The CMS copy is itself
  // paragraphs and headings; sent as server HTML inside a <p>, the browser's
  // parser closes that <p> at the first inner one, the copy lands outside it,
  // and hydration then deletes it as unexpected.
  //
  // Once mounted it moves into the <p> it has always had, set from script
  // the way it always was, so the page ends up exactly as it looked before —
  // the margins several stylesheets give that <p> included. Everywhere else
  // there is no server pass to match, so the <p> is used from the start.
  const renderBody = (htmlContent) =>
    isBlogArticle && !mounted ? (
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    ) : (
      <p dangerouslySetInnerHTML={{ __html: htmlContent }} />
    );

  const processContent = (htmlContent) => {
    if (!htmlContent) return [];
    // Unchanged for every other template: they rendered nothing on the server
    // before and still do.
    if (typeof document === 'undefined' && !isBlogArticle) {
      return [];
    }

    if (authorExists) {
      return (
        <>
          {/*<AuthorBlock data={children?.field_author?.[0]?.author_block} />*/}
          <div className = "authors-wrapper">
            {authorBlockData.map((author, index) => (
              <AuthorBlock key={`author-${author?.field_author_name || 'author'}`} data={author} />
            ))}
          </div>
          {/*<AuthorBlock data={authorBlockData} />*/}
          {renderBody(htmlContent)}
        </>
      );
    }
    return renderBody(htmlContent);
  };

  const content = processContent(
    isItUndefined(children?.field_slug)
      ? children?.field_slug[0]?.value
      : cleanHTML(modifyLinks(isItUndefined(children?.field_template_text), locale))
  );

  const renderTemplateText = () => {
    return <div
        className={
          itemCount > 0
              ? `template-text text-template-items-item-${itemCount} ${authorExists ? 'author-content-block' : ''}`
              : `template-text ${authorExists ? 'author-content-block' : ''}`
        }
    >
      <span className={`${emphasizedTextColor ? emphasizedTextColor : ''}`}>
        {content}
      </span>
      {children?.field_template_quote[0]?.value &&
          renderChildren()
      }
    </div>
  }

  const renderChildren = () => {
    return (
        <div className={'quote'}>
          <div style={{ backgroundColor: color }} className={'quote-line'} />
          <span className={'text-quote'}>
            {children?.field_template_quote[0]?.value}
          </span>
          <div className={getQuoteContainerClass(children)}>
            {isItUndefined(children?.field_template_quote_name) && (
                <span className={'char'}></span>
            )}
            <div className={'sub-cont'}>
              <span className={'name'}>
                {children?.field_template_quote_name[0]?.value}{' '}
              </span>
              {isItUndefined(children?.field_template_quote_name) &&
                  isItUndefined(children?.field_template_quote_job) && (
                      <span>, </span>
                  )}
              <span className={'job'}>
                {children?.field_template_quote_job[0]?.value}
              </span>
            </div>
          </div>
        </div>
    )
  }


  return template || isContentTemplateArticle ? (
      isContentTemplateArticle ?
          <div className={"inner-container"}>
            {renderTemplateText()}
          </div>
          :
          renderTemplateText()
  ) : (
    <div
      style={{ padding: 0, paddingTop: 48 }}
      className={
        itemCount > 0
          ? `template-text text-template-items-item-${itemCount} ${authorExists ? 'author-content-block' : ''}`
          : `template-text ${authorExists ? 'author-content-block' : ''}`
      }
    >
      <div className={'inner-container'}>
        <span className={`${emphasizedTextColor ? emphasizedTextColor : ''}`}>
          {content}
        </span>
        {children?.field_template_quote[0]?.value && renderChildren()}
      </div>
    </div>
  );
};

export default TemplateText;
